import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Heart, X, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import CustomerAuthModal from './CustomerAuthModal';
import {
  hydrate as hydrateFavs,
  isFavorited as isFavoritedSync,
  setFavorited as setFavoritedLocal,
  subscribe as subscribeFavs,
} from '../utils/favoritesStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable heart button — wired to the customer Favorites API.
 *
 *   • Logged in    → click toggles `/customer/favorites/{rid}/toggle`,
 *                    shows a sonner confirmation, and broadcasts the
 *                    new state to every other heart on the page via
 *                    `favoritesStore`.
 *   • Logged out   → click opens a nudge modal asking the user to
 *                    create a free NoFee account or sign in. The modal
 *                    dispatches to the existing global `CustomerAuthModal`
 *                    (signup tab by default — that's the conversion CTA).
 *
 * Variants:
 *   • "overlay"  → absolute-positioned filled circle, designed for the
 *                  top-right of a restaurant card image.
 *   • "inline"   → 36×36 outlined button suitable for hero rows next
 *                  to the restaurant name.
 *
 * The wrapping <Link> on RestaurantCard means we MUST stop both the
 * default navigation AND the bubbling click — otherwise tapping the
 * heart would also open the restaurant page.
 */
const FavoriteHeartButton = ({
  restaurantId,
  restaurantName,
  variant = 'overlay', // 'overlay' | 'inline'
  className = '',
}) => {
  const token = typeof window !== 'undefined'
    ? localStorage.getItem('nofee_customer_token')
    : null;
  const [favorited, setFavorited] = useState(() => isFavoritedSync(restaurantId));
  const [pending, setPending] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');

  // Hydrate the shared favorites set on first mount + subscribe so other
  // heart buttons can update us when they toggle the same restaurant.
  useEffect(() => {
    if (token) hydrateFavs(token);
    const unsub = subscribeFavs(() => {
      setFavorited(isFavoritedSync(restaurantId));
    });
    return unsub;
  }, [token, restaurantId]);

  const closeNudge = useCallback(() => setNudgeOpen(false), []);
  const openAuth = useCallback((mode) => {
    setAuthMode(mode);
    setNudgeOpen(false);
    setAuthOpen(true);
  }, []);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!restaurantId || pending) return;

    if (!token) {
      setNudgeOpen(true);
      return;
    }

    const nextState = !favorited;
    setPending(true);
    // Optimistic update + store sync. Roll back on error.
    setFavorited(nextState);
    setFavoritedLocal(restaurantId, nextState);
    try {
      const res = await axios.post(
        `${API}/customer/favorites/${restaurantId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const serverState = !!res.data?.favorited;
      // Trust the server response if it disagrees with our optimistic value.
      if (serverState !== nextState) {
        setFavorited(serverState);
        setFavoritedLocal(restaurantId, serverState);
      }
      toast.success(
        serverState
          ? 'Restaurant added to Favorites.'
          : 'Removed from Favorites.',
      );
    } catch (err) {
      // Roll back
      setFavorited(!nextState);
      setFavoritedLocal(restaurantId, !nextState);
      toast.error(err?.response?.data?.detail || 'Could not update favorites.');
    } finally {
      setPending(false);
    }
  };

  // Variant-specific button styling. Filled gold heart when favorited,
  // hollow white when not. Drop shadow on overlay so it's readable on
  // any restaurant cover image.
  const baseCls = variant === 'overlay'
    ? 'w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 backdrop-blur-sm border border-white/15 hover:border-yellow-400/60 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] transition-colors'
    : 'w-9 h-9 rounded-xl border bg-white hover:bg-yellow-50 transition-colors';

  const overlayHeartTone = favorited ? 'fill-rose-500 text-rose-500' : 'text-white';
  const inlineHeartTone = favorited ? 'fill-rose-500 text-rose-500' : 'text-slate-600';
  const heartCls = variant === 'overlay' ? overlayHeartTone : inlineHeartTone;
  const inlineBorderCls = favorited ? 'border-rose-300' : 'border-slate-200';

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={pending}
        aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={favorited}
        data-testid={`favorite-heart-${restaurantId}`}
        data-favorited={favorited ? 'true' : 'false'}
        className={`${baseCls} ${variant === 'inline' ? inlineBorderCls : ''} ${className} inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        <Heart className={`w-4 h-4 transition-colors ${heartCls}`} strokeWidth={2.2} />
      </button>

      {/* ─── Nudge modal for logged-out users ─── */}
      {nudgeOpen && (
        <div
          data-testid="favorite-signin-nudge"
          className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) closeNudge(); }}
        >
          <div className="w-full max-w-md bg-zinc-950 border border-yellow-500/25 rounded-3xl shadow-2xl my-auto">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-400" strokeWidth={2.4} />
                <h2 className="text-[15px] font-extrabold text-white">Save this restaurant?</h2>
              </div>
              <button
                type="button"
                onClick={closeNudge}
                data-testid="favorite-nudge-close"
                className="p-1.5 rounded-full hover:bg-white/5"
              >
                <X className="w-4 h-4 text-white/70" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[13.5px] text-white/75 leading-relaxed">
                Save <strong className="text-yellow-300">{restaurantName || 'this restaurant'}</strong> to your favorites.
              </p>
              <p className="text-[13px] text-white/55 leading-relaxed">
                Create a free NoFee account or sign in to save favorite restaurants, reorder faster, and access your personalized experience.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => openAuth('signup')}
                data-testid="favorite-nudge-create-account"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13.5px]"
              >
                Create Free Account <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => openAuth('login')}
                data-testid="favorite-nudge-sign-in"
                className="inline-flex items-center justify-center px-4 py-3 rounded-xl border border-white/15 text-white/85 font-semibold text-[13.5px] hover:bg-white/5"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global customer auth modal — opens on either CTA above. After
          a successful login/signup the user can simply re-tap the
          heart; the optimistic update plus store hydrate path will
          pick up the new token automatically on next click. */}
      <CustomerAuthModal
        open={authOpen}
        initialMode={authMode}
        onClose={() => setAuthOpen(false)}
      />
    </>
  );
};

export default FavoriteHeartButton;
